/**
 * Editable item list for a box detail page.
 * Includes add-item input, and per-item edit/delete.
 */

import { useState } from 'react';
import type { Item } from '@shared/types';

interface ItemListProps {
  items: Item[];
  onAdd: (name: string) => void;
  onUpdate: (id: string, data: { name?: string; quantity?: number }) => void;
  onAdjustQuantity: (id: string, delta: number) => void;
  onDelete: (id: string) => void;
}

export function ItemList({ items, onAdd, onUpdate, onAdjustQuantity, onDelete }: ItemListProps) {
  const [newItemName, setNewItemName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  function handleAdd() {
    const trimmed = newItemName.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setNewItemName('');
  }

  function startEdit(item: Item) {
    setEditingId(item.id);
    setEditName(item.name);
  }

  function saveEdit(id: string) {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== items.find((i) => i.id === id)?.name) {
      onUpdate(id, { name: trimmed });
    }
    setEditingId(null);
  }

  return (
    <div data-testid="item-list">
      {/* Add item input */}
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAdd();
          }}
          placeholder="Add item..."
          className="flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder-text-secondary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          aria-label="New item name"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!newItemName.trim()}
          className="rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Add item"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Items */}
      {items.length === 0 ? (
        <p className="py-8 text-center text-sm text-text-secondary">
          No items yet. Add your first item above.
        </p>
      ) : (
        <ul className="space-y-1" role="list">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-surface/50 group"
              data-testid={`item-${item.id}`}
            >
              {editingId === item.id ? (
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit(item.id);
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                  onBlur={() => saveEdit(item.id)}
                  autoFocus
                  className="flex-1 rounded border border-accent bg-surface px-2 py-1 text-sm text-text-primary focus:outline-none"
                  aria-label="Edit item name"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => startEdit(item)}
                  className="flex-1 text-left text-sm text-text-primary truncate hover:text-accent transition-colors"
                >
                  {item.name}
                </button>
              )}

              {/* Quantity controls */}
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAdjustQuantity(item.id, -1);
                  }}
                  disabled={item.quantity <= 1}
                  className="h-5 w-5 rounded flex items-center justify-center bg-surface hover:bg-accent/20 text-text-secondary hover:text-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label={`Decrease quantity of ${item.name}`}
                  title="Decrease quantity"
                >
                  <svg
                    className="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                  </svg>
                </button>

                <span className="min-w-[2ch] text-center text-xs font-medium text-text-primary tabular-nums">
                  {item.quantity}
                </span>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAdjustQuantity(item.id, 1);
                  }}
                  className="h-5 w-5 rounded flex items-center justify-center bg-surface hover:bg-accent/20 text-text-secondary hover:text-accent transition-colors"
                  aria-label={`Increase quantity of ${item.name}`}
                  title="Increase quantity"
                >
                  <svg
                    className="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              {/* Delete button */}
              <button
                type="button"
                onClick={() => onDelete(item.id)}
                className="shrink-0 p-1 text-text-secondary hover:text-danger opacity-0 group-hover:opacity-100 transition-all"
                aria-label={`Delete ${item.name}`}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
